import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import TabelaApontamentosMontagem from "./components/TabelaApontamentosMontagem";
import blogFetch from "../../axios/config";
import { Apontamento } from "./components/Types";
import TabelaApontamentosGeral from "./components/TabelaApontamentosGeral";

const CENTROS_TRABALHO = [
    '01CORTE', '01HIDCOR', '03SERIG', '05EMPOLV', '06CURVAD', '09IOX_E',
    '09IOX_S', '12P_MONT', '13PRE_IB', '14MONTAG', '15EMBOL',
    '16ACV', '18INSFIN', '51FERRAM', '52HITECH', '57ENGENH', '57LABORA'
];

function toLocalDate(dateString: string): Date {
    const date = new Date(dateString);
    return date;
}

function toLocalDateStr(dateString: string): string {
    return toLocalDate(dateString).toISOString().split('T')[0];
}

// Turnos de trabalho (filtro aplicado no back pela hora de _ts_Creat):
//  1º: 06:00 - 14:00 | 2º: 14:00 - 22:00 | 3º: 22:00 - 06:00 (cruza a meia-noite)
const TURNOS = [
    { value: '1', label: '1º Turno (06:00 - 14:00)' },
    { value: '2', label: '2º Turno (14:00 - 22:00)' },
    { value: '3', label: '3º Turno (22:00 - 06:00)' },
];

// Retorna o turno corrente com base no horário atual (relógio local da fábrica).
function getTurnoAtual(): string {
    const hora = new Date().getHours();
    if (hora >= 6 && hora < 14) return '1';
    if (hora >= 14 && hora < 22) return '2';
    return '3';
}

function ResumoCard({ label, valor, colorClass }: { label: string; valor: number; colorClass: string }): JSX.Element {
    return (
        <div className={`card border-0 text-white ${colorClass} text-center px-3 py-1`} style={{ minWidth: 90 }}>
            <div className="fw-bold lh-1" style={{ fontSize: "1.4rem" }}>{valor}</div>
            <div className="opacity-75" style={{ fontSize: "0.7rem" }}>{label}</div>
        </div>
    );
}

export default function HistoricoApontamentos(): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawSector = searchParams.get('sector') ?? '';
    const centroTrabalhoSelect = CENTROS_TRABALHO.includes(rawSector) ? rawSector : '';
    // Já abre filtrando o dia de hoje (mesma conversão usada na comparação dos apontamentos).
    const [dataFiltro, setDataFiltro] = useState(toLocalDateStr(new Date().toISOString()));
    // Já abre com o turno atual selecionado (ex.: após as 14:00 → 2º turno).
    const [turnoFiltro, setTurnoFiltro] = useState(getTurnoAtual());
    const [apontamentos, setApontamentos] = useState<Apontamento[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pesquisa, setPesquisa] = useState('');

    const handleApontamentos = async () => {
        setApontamentos([]);
        if (!centroTrabalhoSelect) return;
        setIsLoading(true);
        try {
            const params: { CentroTrabalho: string; Turno?: string; Data?: string } = { CentroTrabalho: centroTrabalhoSelect };
            if (turnoFiltro) params.Turno = turnoFiltro; // vazio = todos os turnos
            if (dataFiltro) params.Data = dataFiltro;    // vazio = todos os dias
            const { data } = await blogFetch.get('HistoricoApontamento/GetHistoricoApontamentos', { params });
            if (data && data.length > 0) {
                setApontamentos(data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        handleApontamentos();
    }, [centroTrabalhoSelect, turnoFiltro, dataFiltro]);

    // Centro, turno e data são filtrados no back; a pesquisa livre é aplicada aqui no front.
    const apontamentosFiltrados = useMemo(() => {
        const termo = pesquisa.trim().toLowerCase();
        if (!termo) return apontamentos;
        return apontamentos.filter(ap =>
            [
                ap.ordem_Fabricacao,
                ap.nro_Matricula,
                ap.tipo_Apontamento,
                ap.integration_Status,
                ap.integration_Result,
                ap.centro_Trabalho,
                ap.chave_Modelo,
            ].some(campo => String(campo ?? '').toLowerCase().includes(termo))
        );
    }, [apontamentos, pesquisa]);

    const stats = useMemo(() => ({
        total: apontamentosFiltrados.length,
        ordensUnicas: new Set(apontamentosFiltrados.map(ap => ap.ordem_Fabricacao)).size,
        success: apontamentosFiltrados.filter(ap => ap.integration_Status === 'SUCCESS').length,
        error: apontamentosFiltrados.filter(ap => ap.integration_Status === 'ERROR').length,
        naFila: apontamentosFiltrados.filter(ap => ap.integration_Status === 'NA FILA').length,
    }), [apontamentosFiltrados]);

    const isMontagem = centroTrabalhoSelect === '14MONTAG';
    const showTable = centroTrabalhoSelect !== '';

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body">
                    <h4 className="fw-bold text-primary mb-3">Historico de Apontamentos</h4>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div>
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Centro de Trabalho
                                    </label>
                                    <select
                                        className="form-select form-select-sm shadow-sm"
                                        value={centroTrabalhoSelect}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSearchParams(prev => {
                                                if (value) prev.set('sector', value);
                                                else prev.delete('sector');
                                                return prev;
                                            });
                                        }}
                                    >
                                        <option value="">Escolha um Centro</option>
                                        {CENTROS_TRABALHO.map((centro) => (
                                            <option key={centro} value={centro}>{centro}</option>
                                        ))}
                                    </select>
                                </div>

                                {showTable && (
                                    <div>
                                        <label className="form-label small fw-semibold text-muted mb-1">
                                            Filtrar por Dia
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm shadow-sm"
                                            value={dataFiltro}
                                            onChange={(e) => setDataFiltro(e.target.value)}
                                        />
                                    </div>
                                )}

                                {showTable && (
                                    <div>
                                        <label className="form-label small fw-semibold text-muted mb-1">
                                            Filtrar por Turno
                                        </label>
                                        <select
                                            className="form-select form-select-sm shadow-sm"
                                            value={turnoFiltro}
                                            onChange={(e) => setTurnoFiltro(e.target.value)}
                                        >
                                            <option value="">Todos os turnos</option>
                                            {TURNOS.map((turno) => (
                                                <option key={turno.value} value={turno.value}>{turno.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {showTable && (
                                    <div>
                                        <label className="form-label small fw-semibold text-muted mb-1">
                                            Pesquisar
                                        </label>
                                        <div className="input-group input-group-sm shadow-sm" style={{ minWidth: 240 }}>
                                            <span className="input-group-text"><i className="bi bi-search" /></span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ordem, matrícula, status..."
                                                value={pesquisa}
                                                onChange={(e) => setPesquisa(e.target.value)}
                                            />
                                            {pesquisa && (
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setPesquisa('')}
                                                    title="Limpar pesquisa"
                                                >
                                                    <i className="bi bi-x-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {showTable && (
                                    <button
                                        className="btn btn-sm btn-outline-primary align-self-end"
                                        onClick={handleApontamentos}
                                        disabled={isLoading}
                                        title="Atualizar apontamentos"
                                    >
                                        {isLoading ? (
                                            <span className="spinner-border spinner-border-sm me-1" />
                                        ) : (
                                            <i className="bi bi-arrow-clockwise me-1" />
                                        )}
                                        Atualizar
                                    </button>
                                )}

                                {(dataFiltro || turnoFiltro) && (
                                    <button
                                        className="btn btn-sm btn-outline-secondary align-self-end"
                                        onClick={() => { setDataFiltro(''); setTurnoFiltro(''); }}
                                    >
                                        Limpar filtro
                                    </button>
                                )}

                                {showTable && !isLoading && apontamentos.length > 0 && (
                                    <div className="ms-auto d-flex flex-wrap gap-2 align-items-end">
                                        <ResumoCard label="Apontamentos" valor={stats.total} colorClass="bg-primary" />
                                        <ResumoCard label="Ordens Únicas" valor={stats.ordensUnicas} colorClass="bg-secondary" />
                                        <ResumoCard label="Sucesso" valor={stats.success} colorClass="bg-success" />
                                        <ResumoCard label="Erro" valor={stats.error} colorClass="bg-danger" />
                                        <ResumoCard label="Na Fila" valor={stats.naFila} colorClass="bg-warning text-dark" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {isLoading && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="text-muted mt-2">Carregando apontamentos...</p>
                        </div>
                    )}

                    {!isLoading && isMontagem && (
                        <TabelaApontamentosMontagem apontamentos={apontamentosFiltrados} />
                    )}

                    {!isLoading && showTable && !isMontagem && (
                        <TabelaApontamentosGeral apontamentos={apontamentosFiltrados} />
                    )}
                </div>
            </div>
        </div>
    );
}
